import { CellContext, Column, ColumnDef, Row, FilterFn } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/Common/DataTable';
import { AcademicWorkloadAllocationsWithCalculations } from '#server/models';
import { Button } from '@curtin-dxp/curtin-ui';
import { ActionsDropDown, SheetTrigger } from '@/components';
import { SheetType } from '@/store/types';
import { SelectedStaffState } from '@/store/types';
import { formatHours } from '@/lib/utils';
import { trpc } from '@/data-loaders';
import { toast } from 'sonner';
import { formatNumber } from '@/lib/utils';

interface AcademicsColumnsProps {
  setActiveSheet: (sheet: SheetType) => void;
  actionDropdownRowId: string | null;
  setActionDropdownRowId: (id: string | null) => void;
  setSelectedStaff: (selectedStaff: SelectedStaffState) => void;
  startSnapshot: ReturnType<typeof trpc.startSnapshot.useMutation>;
  activeYear: number;
}

interface formatCellValueProps {
  percent: number;
  totalHours: number;
}

const createHeader = <TData,>(title: string, sortable: boolean) => {
  return ({ column }: { column: Column<TData, unknown> }) => (
    <DataTableColumnHeader column={column} title={title} sortable={sortable} />
  );
};

export const formatCellValue = ({ percent, totalHours }: formatCellValueProps) => {
  return (
    <div className="flex h-[100px] w-[150px] items-center p-[10px]">
      <div className="flex flex-col items-start justify-center gap-y-1">
        <div className={`flex text-b2 font-semibold ${percent < 0 ? 'text-tertiary-red' : 'text-primary-black-90'}`}>
          {formatHours(percent, false, 2)}%
        </div>
        <div className={`flex text-b3 ${totalHours < 0 ? 'text-tertiary-red' : 'text-secondary-grey-02'}`}>
          {formatHours(totalHours, true, 2)}
        </div>
      </div>
    </div>
  );
};

const createCell =
  (key: string) =>
  ({ row }: { row: Row<AcademicWorkloadAllocationsWithCalculations> }) => {
    switch (key) {
      case 'academicName':
        return (
          <div className="flex h-[100px] w-[200px] items-center py-3 pr-[10px]">
            <div className="flex flex-col items-start justify-center">
              <div className="flex text-b1 font-semibold text-primary-black-90">
                {row.original['lastName']}, {row.original['firstName']}
              </div>
              <div className="flex text-b3 text-secondary-grey-02">
                {row.original['curtinId']}, {row.original['workFunctionAbbr']}, {row.original['employmentStatusAbbr']},
                FTE {formatNumber(Number(row.original['fte']))}
              </div>
              <div className="flex text-b3 font-semibold text-primary-black-90">
                {formatHours((row.original['totalAssignedHours'] / row.original['availableHours']) * 100, false, 2)}%
                allocated
              </div>
            </div>
          </div>
        );

      case 'leave':
        return <div className="text-b2 font-semibold">{formatHours(row.original['leaveTotalHours'], true, 2)}</div>;

      case 'engagement':
        return formatCellValue({
          percent: row.original['engagementPercent'],
          totalHours: row.original['engagementTotalHours'],
        });

      case 'isolt':
        return formatCellValue({
          percent: row.original['isoltPercent'],
          totalHours: row.original['isoltTotalHours'],
        });

      case 'leadership':
        return formatCellValue({
          percent: row.original['leadershipTotalPercent'],
          totalHours: row.original['leadershipTotalHours'],
        });

      case 'research':
        return formatCellValue({
          percent: row.original['researchPercent'],
          totalHours: row.original['researchTotalHours'],
        });

      case 'teaching':
        return formatCellValue({
          percent: row.original['teachingPercent'],
          totalHours: row.original['teachingTotalHours'],
        });

      case 'teachingCapacity':
        return formatCellValue({
          percent: row.original['teachingCapacityTotalPercent'],
          totalHours: row.original['teachingCapacityTotalHours'],
        });

      case 'td':
        return formatCellValue({
          percent: row.original['tdTotalPercent'],
          totalHours: row.original['tdTotalHours'],
        });

      case 'trd':
        return formatCellValue({
          percent: row.original['trdTotalPercent'],
          totalHours: row.original['trdTotalHours'],
        });

      default:
      // do nothing
    }
  };

// columnId is needed in filterFn column option (https://tanstack.com/table/v8/docs/guide/column-filtering#filterfns)
const nameFilterFn: FilterFn<AcademicWorkloadAllocationsWithCalculations> = (row, columnId, value) => {
  const name = `${row.original.firstName} ${row.original.lastName} ${row.original.curtinId}`.toLowerCase();
  return name.includes(value.toLowerCase());
};

export const academicsColumns = ({
  setActiveSheet,
  actionDropdownRowId,
  setActionDropdownRowId,
  setSelectedStaff,
  startSnapshot,
  activeYear,
}: AcademicsColumnsProps) => {
  return [
    {
      id: 'academicName',
      accessorFn: (row) => `${row.lastName} ${row.firstName}`,
      filterFn: nameFilterFn,
      sortingFn: 'text',
      header: createHeader('Name', true),
      cell: createCell('academicName'),
    },
    {
      id: 'actions',
      header: () => (
        <div>
          <Button variant="mute" className="border-0" tabIndex={-1}></Button>
        </div>
      ),
      cell: ({ cell }: CellContext<AcademicWorkloadAllocationsWithCalculations, unknown>) => (
        <ActionsDropDown
          options={[
            <SheetTrigger
              className="m-0 w-full p-0 py-[10px] pl-2 text-left"
              onClick={() => {
                setActiveSheet('staff-workload');
                setSelectedStaff({
                  id: cell.row.original.id,
                  firstName: cell.row.original.firstName,
                  lastName: cell.row.original.lastName,
                });
              }}
            >
              Allocate staff workload
            </SheetTrigger>,
            <SheetTrigger
              className="m-0 w-full p-0 py-[10px] pl-2 text-left"
              onClick={() => {
                setActiveSheet('staff');
                setSelectedStaff({
                  id: cell.row.original.id,
                  firstName: cell.row.original.firstName,
                  lastName: cell.row.original.lastName,
                });
              }}
            >
              View staff units
            </SheetTrigger>,
            <SheetTrigger
              className="m-0 w-full p-0 py-[10px] pl-2 text-left"
              onClick={() => {
                setActiveSheet('hdr');
                setSelectedStaff({
                  id: cell.row.original.id,
                  firstName: cell.row.original.firstName,
                  lastName: cell.row.original.lastName,
                });
              }}
            >
              View HDR allocations
            </SheetTrigger>,
            <SheetTrigger
              className="m-0 w-full p-0 py-[10px] pl-2 text-left"
              onClick={async () => {
                try {
                  await startSnapshot.mutateAsync({
                    staffId: cell.row.original.curtinId,
                    firstName: cell.row.original.firstName,
                    lastName: cell.row.original.lastName,
                    year: String(activeYear),
                  });
                  toast.success('Email will be sent shortly.');
                } catch (err) {
                  console.error('Snapshot start failed:', err);
                }
              }}
            >
              Email staff report
            </SheetTrigger>,
          ]}
          actionDropdownRowId={actionDropdownRowId}
          setActionDropdownRowId={setActionDropdownRowId}
          rowId={`academic-${cell.row.id}`}
        />
      ),
    },
    {
      accessorKey: 'leave',
      header: createHeader('Leave', false),
      cell: createCell('leave'),
    },
    {
      accessorKey: 'engagement',
      header: createHeader('Engagement', false),
      cell: createCell('engagement'),
    },
    {
      accessorKey: 'isolt',
      header: createHeader('ISoLT', false),
      cell: createCell('isolt'),
    },
    {
      accessorKey: 'leadership',
      header: createHeader('Leadership', false),
      cell: createCell('leadership'),
    },
    {
      accessorKey: 'research',
      header: createHeader('Research', false),
      cell: createCell('research'),
    },
    {
      accessorKey: 'teaching',
      header: createHeader('Teaching', false),
      cell: createCell('teaching'),
    },
    {
      accessorKey: 'teachingCapacity',
      header: createHeader('Teaching Capacity', false),
      cell: createCell('teachingCapacity'),
    },
    {
      accessorKey: 'td',
      header: createHeader('TD', false),
      cell: createCell('td'),
    },
    {
      accessorKey: 'trd',
      header: createHeader('TRD', false),
      cell: createCell('trd'),
    },
  ] as ColumnDef<AcademicWorkloadAllocationsWithCalculations>[];
};
